import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import { StatCard, Card, HBar, Sparkline, FeedEntry, CorrelationBadges, IncidentChain, SectionTitle } from "../shared";
import { WORLD_MAP_PATH, WORLD_MAP_VIEWBOX } from "../worldMapPaths";

/* ── Network Topology SVG ─────────────────────────────────────────── */
function NetworkTopology() {
    const nodes = [
        { id: "internet", x: 80, y: 50, label: "Internet", color: "#ef4444", r: 22 },
        { id: "firewall", x: 200, y: 50, label: "Firewall", color: "#f97316", r: 22 },
        { id: "dmz", x: 320, y: 50, label: "DMZ", color: "#eab308", r: 22 },
        { id: "core", x: 440, y: 50, label: "Core Switch", color: "#3b82f6", r: 22 },
        { id: "servers", x: 540, y: 15, label: "Servers", color: "#6366f1", r: 18 },
        { id: "workstations", x: 540, y: 50, label: "Workstations", color: "#8b5cf6", r: 18 },
        { id: "iot", x: 540, y: 85, label: "IoT", color: "#06b6d4", r: 18 },
    ];
    const links = [
        ["internet", "firewall"],
        ["firewall", "dmz"],
        ["dmz", "core"],
        ["core", "servers"],
        ["core", "workstations"],
        ["core", "iot"],
    ];
    const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
    return (
        <svg viewBox="0 0 620 100" className="w-full">
            <defs>
                <filter id="topoGlow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <marker
                    id="arrowTopo"
                    viewBox="0 0 10 10"
                    refX="10"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto"
                >
                    <path d="M0 0L10 5L0 10z" fill="rgba(59,130,246,0.4)" />
                </marker>
            </defs>
            {links.map(([from, to]) => {
                const a = nodeMap[from],
                    b = nodeMap[to];
                return (
                    <line
                        key={`${from}-${to}`}
                        x1={a.x}
                        y1={a.y}
                        x2={b.x}
                        y2={b.y}
                        stroke="rgba(59,130,246,0.25)"
                        strokeWidth="2"
                        markerEnd="url(#arrowTopo)"
                    />
                );
            })}
            {nodes.map((n) => (
                <g key={n.id}>
                    <circle
                        cx={n.x}
                        cy={n.y}
                        r={n.r}
                        fill={`${n.color}15`}
                        stroke={n.color}
                        strokeWidth="2"
                        filter="url(#topoGlow)"
                        className="transition-all hover:scale-110"
                        style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                    />
                    <circle cx={n.x} cy={n.y} r={n.r - 6} fill={`${n.color}30`} />
                    <text
                        x={n.x}
                        y={n.y + 3}
                        textAnchor="middle"
                        className="fill-zinc-700 dark:fill-zinc-200"
                        fontSize="7"
                        fontWeight="700"
                    >
                        {n.label}
                    </text>
                </g>
            ))}
            {/* pulse on internet node */}
            <circle cx={80} cy={50} r={22} fill="none" stroke="#ef4444" strokeWidth="1" opacity="0.6">
                <animate attributeName="r" from="22" to="35" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite" />
            </circle>
        </svg>
    );
}

/* ── Top Talkers (horizontal bars) ────────────────────────────────── */
function TopTalkers() {
    const talkers = [
        { ip: "10.0.12.45", bandwidth: 1.8, label: "1.8 GB" },
        { ip: "10.0.8.102", bandwidth: 1.2, label: "1.2 GB" },
        { ip: "10.0.3.78", bandwidth: 0.9, label: "920 MB" },
        { ip: "10.0.15.201", bandwidth: 0.7, label: "710 MB" },
        { ip: "10.0.1.55", bandwidth: 0.5, label: "480 MB" },
    ];
    const max = talkers[0].bandwidth;
    return (
        <div className="space-y-3">
            {talkers.map((t) => (
                <div key={t.ip}>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-mono font-medium text-zinc-700 dark:text-zinc-300">{t.ip}</span>
                        <span className="font-mono text-zinc-500">{t.label}</span>
                    </div>
                    <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                        <div
                            className="h-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700"
                            style={{ width: `${(t.bandwidth / max) * 100}%`, boxShadow: "0 0 8px rgba(6,182,212,0.3)" }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ── Packet Analysis ──────────────────────────────────────────────── */
function PacketAnalysis() {
    const packets = [
        { type: "TCP", pct: 78, color: "from-blue-500 to-blue-600" },
        { type: "UDP", pct: 15, color: "from-cyan-500 to-cyan-600" },
        { type: "ICMP", pct: 4, color: "from-purple-500 to-purple-600" },
        { type: "Other", pct: 3, color: "from-zinc-400 to-zinc-500" },
    ];
    return (
        <div className="space-y-2.5">
            {packets.map((p) => (
                <div
                    key={p.type}
                    className="flex items-center gap-3 rounded-lg border border-zinc-200/30 bg-white/30 px-3 py-2 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/30"
                >
                    <div
                        className={`h-8 w-8 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center text-[10px] font-bold text-white shadow-md`}
                    >
                        {p.type.charAt(0)}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{p.type}</span>
                            <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400">{p.pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                            <div
                                className={`h-1.5 rounded-full bg-gradient-to-r ${p.color}`}
                                style={{ width: `${p.pct}%` }}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ── GeoIP Map (simplified SVG world) ─────────────────────────────── */
function GeoIpMap() {
    const blockedOrigins = [
        { x: 363, y: 27, label: "Moscow", count: 247 },
        { x: 494, y: 39, label: "Beijing", count: 189 },
        { x: 177, y: 38, label: "New York", count: 134 },
        { x: 512, y: 41, label: "Seoul", count: 98 },
        { x: 330, y: 24, label: "Stockholm", count: 45 },
    ];
    return (
        <svg viewBox="0 0 600 140" className="w-full">
            <defs>
                <radialGradient id="dotGlow">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                </radialGradient>
            </defs>
            {/* World map continental outlines */}
            <g transform={`scale(${600 / WORLD_MAP_VIEWBOX.width}, ${140 / WORLD_MAP_VIEWBOX.height})`}>
                <path
                    d={WORLD_MAP_PATH}
                    fill="none"
                    stroke="rgba(59,130,246,0.15)"
                    strokeWidth={1 / (600 / WORLD_MAP_VIEWBOX.width)}
                />
            </g>
            {blockedOrigins.map((o) => (
                <g key={o.label}>
                    <circle cx={o.x} cy={o.y} r="12" fill="url(#dotGlow)" />
                    <circle cx={o.x} cy={o.y} r="3" fill="#ef4444" className="animate-pulse" />
                    <text
                        x={o.x}
                        y={o.y - 8}
                        textAnchor="middle"
                        className="fill-zinc-500 dark:fill-zinc-400"
                        fontSize="6"
                        fontWeight="600"
                    >
                        {o.label}
                    </text>
                    <text
                        x={o.x}
                        y={o.y + 16}
                        textAnchor="middle"
                        className="fill-red-400"
                        fontSize="6"
                        fontWeight="700"
                    >
                        {o.count}
                    </text>
                </g>
            ))}
        </svg>
    );
}

/* ── Bandwidth Timeline (24h area chart) ──────────────────────────── */
function BandwidthTimeline() {
    const data = [
        1.2, 0.8, 0.5, 0.3, 0.4, 0.6, 1.5, 3.2, 4.1, 4.5, 4.8, 5.2, 4.9, 4.6, 4.8, 5.1, 4.7, 4.2, 3.8, 3.2, 2.8, 2.2,
        1.8, 1.4,
    ];
    const max = 6;
    const w = 560,
        h = 80;
    const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * h}`).join(" ");
    const areaPath = `M0,${h} L${points
        .split(" ")
        .map((p) => p)
        .join(" L")} L${w},${h}Z`;
    const linePath = `M${points.split(" ").join(" L")}`;
    const anomalyIdx = 11; // 11:00 spike
    const ax = (anomalyIdx / (data.length - 1)) * w;
    const ay = h - (data[anomalyIdx] / max) * h;
    return (
        <svg viewBox={`0 0 ${w} ${h + 20}`} className="w-full">
            <defs>
                <linearGradient id="bwFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="bwStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#3b82f6" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#bwFill)" />
            <path d={linePath} fill="none" stroke="url(#bwStroke)" strokeWidth="2" strokeLinecap="round" />
            {/* anomaly annotation */}
            <circle cx={ax} cy={ay} r="4" fill="#ef4444" opacity="0.8" />
            <line
                x1={ax}
                y1={ay + 4}
                x2={ax}
                y2={h}
                stroke="#ef4444"
                strokeWidth="1"
                strokeDasharray="3,3"
                opacity="0.4"
            />
            <text x={ax + 6} y={ay - 2} className="fill-red-400" fontSize="7" fontWeight="600">
                5.2 Gbps anomaly
            </text>
            {/* x-axis labels */}
            {[0, 6, 12, 18, 23].map((hr) => (
                <text
                    key={hr}
                    x={(hr / 23) * w}
                    y={h + 14}
                    textAnchor="middle"
                    className="fill-zinc-400"
                    fontSize="7"
                >{`${hr}:00`}</text>
            ))}
        </svg>
    );
}

/* ── DPI Table ────────────────────────────────────────────────────── */
function DpiTable() {
    const protocols = [
        { name: "HTTPS/TLS", traffic: "42%", risk: "Low", riskColor: "green" as const },
        { name: "HTTP", traffic: "18%", risk: "Medium", riskColor: "amber" as const },
        { name: "DNS", traffic: "14%", risk: "Low", riskColor: "green" as const },
        { name: "SMB/CIFS", traffic: "9%", risk: "High", riskColor: "red" as const },
        { name: "RDP", traffic: "6%", risk: "High", riskColor: "red" as const },
        { name: "SSH", traffic: "5%", risk: "Low", riskColor: "green" as const },
        { name: "FTP", traffic: "3%", risk: "Critical", riskColor: "red" as const },
        { name: "Other", traffic: "3%", risk: "Medium", riskColor: "amber" as const },
    ];
    return (
        <div className="space-y-2">
            {protocols.map((p) => (
                <div
                    key={p.name}
                    className="flex items-center gap-3 rounded-lg border border-zinc-200/30 bg-white/30 px-3 py-2 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/30"
                >
                    <span className="w-24 text-xs font-semibold text-zinc-700 dark:text-zinc-300">{p.name}</span>
                    <div className="flex-1">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                            <div
                                className="h-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                                style={{ width: p.traffic }}
                            />
                        </div>
                    </div>
                    <span className="w-10 text-right font-mono text-xs text-zinc-500">{p.traffic}</span>
                    <Badge color={p.riskColor}>{p.risk}</Badge>
                </div>
            ))}
        </div>
    );
}

/* ── Main Dashboard ───────────────────────────────────────────────── */
export function NdrDashboard({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const ndrEvents = UNIFIED_EVENTS.filter((e) => e.system === "ndr");
    const riskColor = { high: "red", medium: "yellow", low: "zinc" } as const;
    const actionMap: Record<string, "blocked" | "flagged" | "allowed"> = {
        "NDR-421": "blocked",
        "NDR-418": "flagged",
        "NDR-415": "blocked",
        "NDR-412": "flagged",
        "NDR-408": "allowed",
    };
    const actionColor = { blocked: "red", flagged: "amber", allowed: "green" } as const;
    const protoMap: Record<string, string> = {
        "NDR-421": "TCP/443",
        "NDR-418": "SMB",
        "NDR-415": "DNS",
        "NDR-412": "RDP",
        "NDR-408": "TCP/8080",
    };
    const srcMap: Record<string, string> = {
        "NDR-421": "10.0.12.45",
        "NDR-418": "10.0.8.102",
        "NDR-415": "10.0.3.78",
        "NDR-412": "10.0.15.201",
        "NDR-408": "10.0.1.55",
    };
    const dstMap: Record<string, string> = {
        "NDR-421": "185.143.223.1",
        "NDR-418": "10.0.8.1",
        "NDR-415": "91.215.85.12",
        "NDR-412": "10.0.15.254",
        "NDR-408": "104.21.35.12",
    };

    return (
        <>
            <Heading>{t("ndr.title")}</Heading>

            {/* ── Stat Cards ──────────────────────────────────────────── */}
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label={t("ndr.monitoredDevices")} value="1,247" />
                <StatCard label={t("ndr.anomaliesToday")} value="23" trend="-5 vs. yesterday" trendDown />
                <StatCard label={t("ndr.bandwidth")} value="4.8 Gbps" />
                <StatCard label={t("ndr.blockedConn")} value="156" trend="+18%" />
            </div>

            {/* ── Original 3-col grid ─────────────────────────────────── */}
            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <Card>
                    <Subheading>Protocol Distribution</Subheading>
                    <div className="mt-4">
                        <HBar
                            items={[
                                { label: "HTTPS", value: 14280, max: 14280, color: "bg-blue-700 dark:bg-blue-300" },
                                { label: "DNS", value: 8932, max: 14280, color: "bg-blue-500" },
                                { label: "SMB", value: 3421, max: 14280, color: "bg-blue-400" },
                                { label: "RDP", value: 1847, max: 14280, color: "bg-blue-500" },
                                { label: "SSH", value: 982, max: 14280, color: "bg-blue-300 dark:bg-blue-600" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Bandwidth (7 days)</Subheading>
                    <div className="mt-4">
                        <Sparkline data={[3.2, 4.1, 3.8, 4.8, 5.2, 3.9, 4.8]} />
                        <div className="mt-2 flex justify-between text-xs text-zinc-400">
                            <span>Mon</span>
                            <span>Tue</span>
                            <span>Wed</span>
                            <span>Thu</span>
                            <span>Fri</span>
                            <span>Sat</span>
                            <span>Sun</span>
                        </div>
                    </div>
                </Card>
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:21"
                            icon={ShieldCheckIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="soc"
                            onNavigate={onNavigate}
                        >
                            C2 callback blocked — SOC Alert #1247
                        </FeedEntry>
                        <FeedEntry
                            time="14:15"
                            icon={BugAntIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="pentests"
                            onNavigate={onNavigate}
                        >
                            SMB lateral movement matches PT-003
                        </FeedEntry>
                        <FeedEntry
                            time="14:02"
                            icon={EnvelopeIcon}
                            color="bg-blue-500"
                            linkedSystem="mail"
                            onNavigate={onNavigate}
                        >
                            DNS tunneling linked to Mail quarantine
                        </FeedEntry>
                        <FeedEntry
                            time="13:48"
                            icon={LockClosedIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="ztna"
                            onNavigate={onNavigate}
                        >
                            RDP anomaly — ZTNA restricted segment
                        </FeedEntry>
                        <FeedEntry
                            time="13:30"
                            icon={EnvelopeIcon}
                            color="bg-blue-500 dark:bg-blue-400"
                            linkedSystem="phishing"
                            onNavigate={onNavigate}
                        >
                            HTTP beacon from phishing sim clicker
                        </FeedEntry>
                    </div>
                </Card>
            </div>

            {/* ── Network Topology ─────────────────────────────────────── */}
            <SectionTitle title="Network Topology" description="Logical network map with monitored segments" />
            <Card>
                <NetworkTopology />
            </Card>

            {/* ── Top Talkers + Packet Analysis ───────────────────────── */}
            <SectionTitle title="Traffic Analysis" description="Top internal talkers and packet type distribution" />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Top Talkers by Bandwidth</Subheading>
                    <div className="mt-4">
                        <TopTalkers />
                    </div>
                </Card>
                <Card>
                    <Subheading>Packet Type Distribution</Subheading>
                    <div className="mt-4">
                        <PacketAnalysis />
                    </div>
                </Card>
            </div>

            {/* ── GeoIP Map ───────────────────────────────────────────── */}
            <SectionTitle
                title="GeoIP Blocked Origins"
                description="Geographic distribution of blocked external connections"
            />
            <Card>
                <GeoIpMap />
            </Card>

            {/* ── Bandwidth Timeline ──────────────────────────────────── */}
            <SectionTitle title="Bandwidth Timeline (24h)" description="Network throughput with anomaly annotations" />
            <Card>
                <BandwidthTimeline />
            </Card>

            {/* ── DPI ─────────────────────────────────────────────────── */}
            <SectionTitle
                title="Deep Packet Inspection"
                description="Detected application protocols with risk assessment"
            />
            <Card>
                <DpiTable />
            </Card>

            {/* ── Cross-System Incident Chains (original) ─────────────── */}
            <Card className="mt-6">
                <Subheading>Cross-System Incident Chains</Subheading>
                <div className="mt-4 space-y-3">
                    <div>
                        <Text className="mb-1 text-xs font-medium sm:text-xs">NET-421: C2 Chain</Text>
                        <IncidentChain
                            onNavigate={onNavigate}
                            steps={[
                                { system: "mail", label: "Malware delivered" },
                                { system: "ndr", label: "C2 detected" },
                                { system: "soc", label: "Alert escalated" },
                                { system: "ztna", label: "Session killed" },
                            ]}
                        />
                    </div>
                    <div>
                        <Text className="mb-1 text-xs font-medium sm:text-xs">NET-418: Lateral Movement</Text>
                        <IncidentChain
                            onNavigate={onNavigate}
                            steps={[
                                { system: "pentests", label: "PT-003 vector" },
                                { system: "ndr", label: "SMB anomaly" },
                                { system: "soc", label: "Incident" },
                                { system: "ztna", label: "RDP restricted" },
                                { system: "phishing", label: "User awareness" },
                            ]}
                        />
                    </div>
                </div>
            </Card>

            {/* ── Anomalies Table (original) ──────────────────────────── */}
            <Subheading className="mt-10">{t("ndr.anomalies")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("ndr.time")}</TableHeader>
                        <TableHeader>{t("ndr.sourceIp")}</TableHeader>
                        <TableHeader>{t("ndr.destIp")}</TableHeader>
                        <TableHeader>{t("ndr.protocol")}</TableHeader>
                        <TableHeader>{t("ndr.risk")}</TableHeader>
                        <TableHeader>{t("ndr.action")}</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ndrEvents.map((e) => (
                        <TableRow key={e.id}>
                            <TableCell className="font-mono text-zinc-500">{e.time}</TableCell>
                            <TableCell className="font-mono">{srcMap[e.id] ?? "—"}</TableCell>
                            <TableCell className="font-mono">{dstMap[e.id] ?? "—"}</TableCell>
                            <TableCell className="font-mono">{protoMap[e.id] ?? "—"}</TableCell>
                            <TableCell>
                                <Badge color={riskColor[e.severity === "critical" ? "high" : e.severity]}>
                                    {t(`soc.${e.severity}`)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge color={actionColor[actionMap[e.id] ?? "allowed"]}>
                                    {t(`ndr.${actionMap[e.id] ?? "allowed"}`)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button
                                    onClick={() => onInvestigate(e)}
                                    className="text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
